import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Loader2, Users, Search, Phone, Mail, Linkedin, Building2 } from "lucide-react";

export default function ContactsPage() {
  const { token } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.getContacts(token).then((d) => setContacts(d.items || [])).finally(() => setLoading(false));
  }, [token]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.company_names || []).some((cn) => cn.toLowerCase().includes(q)) ||
      (c.email || "").toLowerCase().includes(q)
    );
  }, [contacts, search]);

  return (
    <div className="space-y-6 animate-in" data-testid="contacts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Users className="w-7 h-7" />Contactos</h1>
          <p className="text-muted-foreground mt-1">Directorio de personas del Observatorio de Ecosistema (solo lectura, sincronizado desde Airtable)</p>
        </div>
        <Badge variant="outline" data-testid="contacts-count">{contacts.length} contactos</Badge>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar por nombre, empresa o email..."
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="contacts-search-input"
        />
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="contacts-empty">Sin contactos para mostrar.</p>
      ) : (
        <div className="border rounded-lg divide-y" data-testid="contacts-list">
          {filtered.map((c) => (
            <div key={c.id} className="p-4 flex flex-wrap items-center gap-x-6 gap-y-1" data-testid={`contact-row-${c.id}`}>
              <p className="font-medium w-48 truncate">{c.name}</p>
              {(c.company_names || []).length > 0 && (
                <span className="text-sm text-muted-foreground flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{c.company_names.join(", ")}</span>
              )}
              {c.email && <span className="text-sm text-muted-foreground flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{c.email}</span>}
              {c.phone && <span className="text-sm text-muted-foreground flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{c.phone}</span>}
              {c.linkedin && (
                <a href={c.linkedin} target="_blank" rel="noreferrer" className="text-sm text-blue-600 flex items-center gap-1 hover:underline">
                  <Linkedin className="w-3.5 h-3.5" />LinkedIn
                </a>
              )}
              {c.interactions_count > 0 && (
                <Badge variant="secondary" className="text-[10px]">
                  {c.interactions_count} {c.interactions_count === 1 ? "interacción" : "interacciones"}
                </Badge>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
